<template>
    <div class="layout-px-spacing">
        <teleport to="#breadcrumb">
            <ul class="navbar-nav flex-row">
                <li>
                    <div class="page-header">
                        <nav class="breadcrumb-one" aria-label="breadcrumb">
                            <ol class="breadcrumb">
                                <li class="breadcrumb-item active" aria-current="page"><span>View Violators</span></li>
                            </ol>
                        </nav>
                    </div>
                </li>
            </ul>
        </teleport>

        <div class="row layout-top-spacing">
            <div class="col-xl-12 col-lg-12 col-md-12 col-12 layout-spacing">
                <div class="panel br-4">
                    <div class="panel-body">
                        <div class="d-flex justify-content-between align-items-center mb-3">
                            <h6>Here are all recorded violators. <br><br><strong>You have {{ filteredDrivers.length }}
                                    so far!</strong></h6>
                            <div class="search-container">
                                <input v-model="searchQuery" type="text" class="form-control"
                                    placeholder="Search by driver name..." style="width: 300px;"
                                    @input="handleSearchInput">
                            </div>
                        </div>

                        <div class="table-responsive">
                            <table class="table table-hover table-bordered">
                                <thead>
                                    <tr>
                                        <th>Full Name</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="violator in paginatedDrivers" :key="violator.employee_id">
                                        <td>{{ violator.driver }}</td>
                                    </tr>
                                    <tr v-if="paginatedDrivers.length === 0">
                                        <td class="text-center text-muted">No drivers found</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <!-- Pagination -->
                        <div class="row mt-3" v-if="filteredDrivers.length > 0">
                            <div class="col-md-6">
                                <div class="dataTables_info">
                                    Showing {{ startItem }} to {{ endItem }} of {{ filteredDrivers.length }} entries
                                </div>
                            </div>
                            <div class="col-md-6">
                                <nav aria-label="Page navigation" class="float-end">
                                    <ul class="pagination">
                                        <li class="page-item" :class="{ disabled: currentPage === 1 }">
                                            <button class="page-link" @click="prevPage">&laquo;</button>
                                        </li>
                                        <li class="page-item" v-for="page in visiblePages" :key="page"
                                            :class="{ active: currentPage === page }">
                                            <button class="page-link" @click="goToPage(page)">{{ page }}</button>
                                        </li>
                                        <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                                            <button class="page-link" @click="nextPage">&raquo;</button>
                                        </li>
                                    </ul>
                                </nav>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.layout-px-spacing {
    min-height: calc(100vh - 170px) !important;
}

.cursor-pointer {
    cursor: pointer;
}

.search-container {
    margin-left: auto;
    margin-right: 20px;
}

.pagination {
    margin-bottom: 0;
}

.page-item.active .page-link {
    background-color: #4361ee;
    border-color: #4361ee;
}

.page-link {
    color: #4361ee;
}
</style>

<script setup>
import { onMounted, ref, computed } from "vue";
import { useRouter } from "vue-router";
import { useMeta } from "@/composables/use-meta";
import axios from "axios";
import { BASE_URL_2 } from "@/Config/config";

useMeta({ title: "View Violators" });

const router = useRouter();
const drivers = ref([]); 
const searchQuery = ref("");
const currentPage = ref(1);
const itemsPerPage = 25;

// Fetch Employees
const loadEmployees = async () => {
    try {
        const response = await axios.get(`${BASE_URL_2}/employee/driver/show`);
        drivers.value = Array.isArray(response.data) ? response.data.drivers : [];
        // drivers.value = response.data.drivers;
    } catch (error) {
        console.error("Error fetching drivers:", error);
        drivers.value = []; 
    }
};

// Filter drivers based on search query - always returns an array
const filteredDrivers = computed(() => {
    if (!Array.isArray(drivers.value)) return [];
    
    if (!searchQuery.value) {
        return drivers.value;
    }
    const query = searchQuery.value.toLowerCase();
    return drivers.value.filter(driver => 
        driver?.driver?.toLowerCase().includes(query)
    );
});

// Pagination computed properties
const totalPages = computed(() => {
    return Math.ceil(filteredDrivers.value.length / itemsPerPage);
});

const paginatedDrivers = computed(() => {
    // Ensure we're working with an array
    const items = Array.isArray(filteredDrivers.value) ? filteredDrivers.value : [];
    const start = (currentPage.value - 1) * itemsPerPage;
    const end = start + itemsPerPage;
    return items.slice(start, end);
});

const startItem = computed(() => {
    return (currentPage.value - 1) * itemsPerPage + 1;
});

const endItem = computed(() => {
    const items = Array.isArray(filteredDrivers.value) ? filteredDrivers.value : [];
    const end = currentPage.value * itemsPerPage;
    return end > items.length ? items.length : end;
});

// Handle search input - reset to first page
const handleSearchInput = () => {
    currentPage.value = 1;
};

// Pagination methods remain the same
const goToPage = (page) => {
    currentPage.value = page;
};

const nextPage = () => {
    if (currentPage.value < totalPages.value) {
        currentPage.value++;
    }
};

const prevPage = () => {
    if (currentPage.value > 1) {
        currentPage.value--;
    }
};

// Load on mount
onMounted(() => {
    loadEmployees();
});
</script>